import React, { useEffect, useState } from 'react';
import { Box } from '@abyss/web/ui/Box';
import { Label } from '@abyss/web/ui/Label';
import { Button } from '@abyss/web/ui/Button';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { CookieService } from '@src/api/common/CookieService';
import { API_URL } from '@src/constants/BaseUrls';
import { msalInstance } from '@src/api/common/MsalService';

interface PowerBIReportPanelProps {
  /** The Acquired Entity name — used as the `ae` query param when fetching the embed URL. */
  aeName: string;
  /** Resolved API base URL (e.g. http://localhost:5000 or the configured API_URL). */
  baseUrl?: string;
}

const POWER_BI_REPORT_PAGES = [
  {
    label: 'Site Map',
    pageName: '871058e612192edabdbc',
  },
  {
    label: 'User Map',
    pageName: 'aed6669cee9d6ccac671',
  },
  {
    label: 'Warehouse Map',
    pageName: '191e5d03e0603e30545e',
  },
];

/** Returns Authorization header from the token stored after login. */
const getAuthHeaders = (): HeadersInit => {
  const token = CookieService.GetCookie('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Mirrors the same helper used in ResultsScreen — handles the edge case where
 * the configured base URL ends with "/api" to avoid double-prefixing.
 */
const buildApiUrl = (baseUrl: string, path: string): string => {
  if (baseUrl.endsWith('/api') && path.startsWith('/api/')) {
    return `${baseUrl}${path.slice(4)}`;
  }
  return `${baseUrl}${path}`;
};

const normalizeBaseUrl = (url?: string): string => {
  if (!url) {
    return '';
  }
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

const resolvePowerBiApiBase = (baseUrl?: string): string => {
  const configured = normalizeBaseUrl(baseUrl || API_URL);
  if (!configured || configured.includes('xyz-dev.com')) {
    return 'http://localhost:5000';
  }
  return configured;
};

const appendPageName = (embedUrl: string, pageName: string): string => {
  const separator = embedUrl.includes('?') ? '&' : '?';
  return `${embedUrl}${separator}pageName=${encodeURIComponent(pageName)}`;
};

/**
 * Acquires a Power BI-scoped AAD token using the existing MSAL instance.
 * Returns null when the app registration does not have Power BI permissions.
 */
const acquirePowerBiToken = async (): Promise<string | null> => {
  try {
    const account =
      msalInstance.getActiveAccount() ||
      msalInstance.getAllAccounts()[0] ||
      null;
    if (!account) return null;
    const result = await msalInstance.acquireTokenSilent({
      scopes: ['https://analysis.windows.net/powerbi/api/Report.Read.All'],
      account,
    });
    return result.accessToken;
  } catch {
    return null;
  }
};

const extractReportMeta = (
  embedUrl: string
): { reportId: string | null; groupId: string | null } => {
  try {
    const url = new URL(embedUrl);
    return {
      reportId: url.searchParams.get('reportId'),
      groupId: url.searchParams.get('groupId'),
    };
  } catch {
    return { reportId: null, groupId: null };
  }
};

/**
 * Fetches Power BI report pages via the REST API and returns a map of
 * displayName → internal pageName (e.g. "Site Cluster Overview" → "ReportSection1234…").
 * Returns null when the token cannot be acquired or the request fails.
 */
const fetchPowerBiPageNames = async (
  embedUrl: string
): Promise<Record<string, string> | null> => {
  const pbiToken = await acquirePowerBiToken();
  if (!pbiToken) return null;

  const { reportId, groupId } = extractReportMeta(embedUrl);
  if (!reportId) return null;

  try {
    const apiUrl = groupId
      ? `https://api.powerbi.com/v1.0/myorg/groups/${groupId}/reports/${reportId}/pages`
      : `https://api.powerbi.com/v1.0/myorg/reports/${reportId}/pages`;

    const response = await fetch(apiUrl, {
      headers: { Authorization: `Bearer ${pbiToken}` },
    });
    if (!response.ok) return null;

    const data = await response.json();
    const pages = (data?.value ?? []) as Array<{
      name: string;
      displayName: string;
    }>;

    return pages.reduce<Record<string, string>>((map, page) => {
      // eslint-disable-next-line no-param-reassign
      map[page.displayName] = page.name;
      return map;
    }, {});
  } catch {
    return null;
  }
};

/**
 * Fetches a Power BI embed URL from the backend for the given AE, then renders
 * the report inside a plain <iframe>. No Power BI JS SDK is required.
 *
 * The backend endpoint (GET /api/powerbi/embed-url?ae=<aeName>) returns:
 *   { embedUrl: string, aeName: string }
 *
 * The embedUrl already contains autoAuth=true and the AE row-level filter,
 * so the user will be signed in via Microsoft SSO automatically.
 */
export const PowerBIMapViewer: React.FC<PowerBIReportPanelProps> = ({
  aeName,
  baseUrl,
}) => {
  const resolvedBaseUrl = resolvePowerBiApiBase(baseUrl);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(
    POWER_BI_REPORT_PAGES[0].pageName,
  );
  /**
   * Maps Power BI page display names to their internal page names
   * (e.g. "Site Cluster Overview" → "ReportSection1234…").
   * Null until the REST API call resolves; remains null when the app
   * registration does not have Power BI permissions.
   */
  const [pageNameMap, setPageNameMap] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    if (!aeName) {
      setError('No entity name provided.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setEmbedUrl(null);
    setPageNameMap(null);

    const fetchEmbedUrl = async () => {
      try {
        const url = buildApiUrl(
          resolvedBaseUrl,
          `/api/powerbi/embed-url?ae=${encodeURIComponent(aeName)}`,
        );
        const response = await fetch(url, { headers: getAuthHeaders() });

        if (cancelled) return;

        if (!response.ok) {
          setError(`Failed to load Power BI report (HTTP ${response.status}).`);
          setLoading(false);
          return;
        }

        const body = await response.json();
        if (cancelled) return;

        const fetchedUrl: string = body?.embedUrl || '';
        if (!fetchedUrl) {
          setError('No embed URL returned from server.');
          setLoading(false);
          return;
        }

        setEmbedUrl(fetchedUrl);

        // Fetch internal Power BI page names in parallel (non-blocking).
        // When the app registration has Power BI API permissions, this resolves
        // the real internal page IDs so the pageName URL parameter works correctly.
        // Falls back gracefully to display-name passthrough when unavailable.
        fetchPowerBiPageNames(fetchedUrl).then((map) => {
          if (!cancelled) {
            setPageNameMap(map);
          }
        });
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Unable to load Power BI report.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchEmbedUrl();
    return () => {
      cancelled = true;
    };
  }, [aeName, resolvedBaseUrl]);

  // Resolve the internal Power BI page name from the display name.
  // When pageNameMap is available (Power BI REST API responded), we use the real
  // internal ID so Power BI actually navigates to the correct page.
  // Falls back to the display name when the map hasn't loaded yet.
  const resolvedPageName = pageNameMap?.[selectedPage] ?? selectedPage;
  const iframeSrc = embedUrl ? appendPageName(embedUrl, resolvedPageName) : '';
  const selectedPageLabel =
    POWER_BI_REPORT_PAGES.find((p) => p.pageName === selectedPage)?.label ?? selectedPage;

  const pageSelector = (
    <Box css={{ marginBottom: '12px', maxWidth: '420px' }}>
      <Label
        size="md"
        color="$gray8"
        fontWeight="bold"
        css={{ display: 'block', marginBottom: '0', paddingLeft: '12px' }}
      >
        View Maps
      </Label>
      <select
        value={selectedPage}
        onChange={(event) => setSelectedPage(event.target.value)}
        style={{
          display: 'block',
          width: '100%',
          height: '40px',
          borderRadius: '0',
          border: '1px solid #c6d4e8',
          padding: '0 12px',
          paddingRight: '34px',
          fontSize: '14px',
          color: '#2d3a4a',
          backgroundColor: '#fff',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27%2360758f%27 d=%27M2.2 4.2a.75.75 0 0 1 1.06 0L6 6.94 8.74 4.2a.75.75 0 1 1 1.06 1.06L6.53 8.53a.75.75 0 0 1-1.06 0L2.2 5.26a.75.75 0 0 1 0-1.06z%27/%3E%3C/svg%3E")',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 10px center',
          backgroundSize: '14px 14px',
        }}
      >
        {POWER_BI_REPORT_PAGES.map((page) => (
          <option key={page.pageName} value={page.pageName}>
            {page.label}
          </option>
        ))}
      </select>
    </Box>
  );

  if (loading) {
    return (
      <>
        {pageSelector}
        <Box
          css={{
            height: '640px',
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            border: '1px solid #d8dde6',
            padding: '24px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              height: '100%',
            }}
          >
            <div
              style={{
                width: '36%',
                height: '28px',
                borderRadius: '4px',
                background:
                  'linear-gradient(90deg, #e8edf4 25%, #f7f9fc 50%, #e8edf4 75%)',
                backgroundSize: '200% 100%',
                animation: 'powerBiPanelSkeleton 1.2s ease-in-out infinite',
              }}
            />
            <div
              style={{
                flex: 1,
                borderRadius: '6px',
                background:
                  'linear-gradient(90deg, #e8edf4 25%, #f7f9fc 50%, #e8edf4 75%)',
                backgroundSize: '200% 100%',
                animation: 'powerBiPanelSkeleton 1.2s ease-in-out infinite',
              }}
            />
            <Label size="sm" color="$gray6">
              Loading Power BI report...
            </Label>
          </div>
          <style>{`@keyframes powerBiPanelSkeleton { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        {pageSelector}
        <Box
          css={{
            height: '640px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            border: '1px solid #d8dde6',
          }}
        >
          <Label size="sm" color="$red7">
            {error}
          </Label>
        </Box>
      </>
    );
  }

  return (
    <>
      {isFullscreen && iframeSrc && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            backgroundColor: '#000',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 16px',
              backgroundColor: '#002677',
              color: '#fff',
              flexShrink: 0,
              gap: 16,
              boxShadow: '0 1px 8px rgba(0,0,0,0.18)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minWidth: 0,
                flex: '1 1 auto',
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: '18px',
                  whiteSpace: 'nowrap',
                }}
              >
                View Maps
              </span>
              <select
                value={selectedPage}
                onChange={(event) => setSelectedPage(event.target.value)}
                aria-label="Select map view"
                style={{
                  width: 'min(320px, 46vw)',
                  height: '34px',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.55)',
                  padding: '0 32px 0 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#002677',
                  backgroundColor: '#fff',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27%23002677%27 d=%27M2.2 4.2a.75.75 0 0 1 1.06 0L6 6.94 8.74 4.2a.75.75 0 1 1 1.06 1.06L6.53 8.53a.75.75 0 0 1-1.06 0L2.2 5.26a.75.75 0 0 1 0-1.06z%27/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  backgroundSize: '14px 14px',
                  cursor: 'pointer',
                }}
              >
                {POWER_BI_REPORT_PAGES.map((page) => (
                  <option
                    key={page.pageName}
                    value={page.pageName}
                    style={{ color: '#002677', backgroundColor: '#fff' }}
                  >
                    {page.label}
                  </option>
                ))}
              </select>
            </div>
            <Button
              size="$sm"
              variant="outline"
              onClick={() => setIsFullscreen(false)}
              css={{
                backgroundColor: '$primary2',
                color: '$primary1',
                borderColor: '$primary2',
                flexShrink: 0,
              }}
            >
              Exit Full Screen
            </Button>
          </div>
          <iframe
            title={`${selectedPageLabel} (fullscreen)`}
            src={iframeSrc}
            style={{ width: '100%', flex: 1, border: 0 }}
            allowFullScreen
            allow="fullscreen"
          />
        </div>
      )}
      {pageSelector}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          margin: '-4px 0 8px',
        }}
      >
        <Button onClick={() => setIsFullscreen(true)}>
          <IconSymbol icon="fullscreen" size={14} />
          Full Screen
        </Button>
      </div>
      <Box
        css={{
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #d8dde6',
          background: '#f5f7fa',
        }}
      >
        {/* key includes resolvedPageName so the iframe remounts on every page
            change — Power BI embedded reports ignore src changes for navigation
            and require a full reload to land on the correct page. */}
        <iframe
          key={`${aeName}-${resolvedPageName}`}
          title={`Power BI Report - ${selectedPageLabel}`}
          src={iframeSrc}
          width="100%"
          height="640"
          frameBorder={0}
          allowFullScreen
          allow="fullscreen"
          style={{ display: 'block', border: 'none' }}
        />
      </Box>
    </>
  );
};

export const PowerBIReportPanel = PowerBIMapViewer;
